import { apiClient } from './apiClient';
import {
    Tenant,
    TenantCreateRequest,
    TenantUpdateRequest,
    UserAdminView,
    UserCreateRequest,
    UserUpdateRequest,
    InviteRequest,
    Invitation,
    FeatureFlag,
    FeatureFlagCreateRequest,
    FeatureFlagUpdateRequest,
    LLMConfig,
    LLMConfigCreateRequest,
    LLMConfigUpdateRequest,
    LLMApiKey,
    LLMApiKeyCreateRequest,
    ModelPricing,
    ModelPricingCreateRequest,
    ModelPricingUpdateRequest,
    LLMProvidersResponse,
    UsageStatsRow
} from './types';

// ==========================================
// Tenants
// ==========================================

export const getTenants = async (): Promise<Tenant[]> => {
    const response = await apiClient.get<Tenant[]>('/api/v1/admin/tenants');
    return response.data;
};

export const createTenant = async (data: TenantCreateRequest): Promise<Tenant> => {
    const response = await apiClient.post<Tenant>('/api/v1/admin/tenants', data);
    return response.data;
};

export const updateTenant = async (tenantId: string, data: TenantUpdateRequest): Promise<Tenant> => {
    const response = await apiClient.put<Tenant>(`/api/v1/admin/tenants/${tenantId}`, data);
    return response.data;
};

// ==========================================
// Users
// ==========================================

export const getUsers = async (): Promise<UserAdminView[]> => {
    const response = await apiClient.get<UserAdminView[]>('/api/v1/admin/users');
    return response.data;
};

export const createUser = async (data: UserCreateRequest): Promise<UserAdminView> => {
    const response = await apiClient.post<UserAdminView>('/api/v1/admin/users', data);
    return response.data;
};

export const updateUser = async (userId: string, data: UserUpdateRequest): Promise<UserAdminView> => {
    const response = await apiClient.put<UserAdminView>(`/api/v1/admin/users/${userId}`, data);
    return response.data;
};

export const resetUserMfa = async (userId: string): Promise<void> => {
    await apiClient.post(`/api/v1/admin/users/${userId}/mfa/reset`);
};

// ==========================================
// Invitations
// ==========================================

export const getInvitations = async (): Promise<Invitation[]> => {
    const response = await apiClient.get<Invitation[]>('/api/v1/admin/invitations');
    return response.data;
};

export const sendInvitation = async (data: InviteRequest): Promise<Invitation> => {
    const response = await apiClient.post<Invitation>('/api/v1/admin/invitations', data);
    return response.data;
};

export const revokeInvitation = async (token: string): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/invitations/${token}`);
};

export const resendInvitation = async (token: string): Promise<Invitation> => {
    const response = await apiClient.post<Invitation>(`/api/v1/admin/invitations/${token}/resend`);
    return response.data;
};

// ==========================================
// Feature Flags
// ==========================================

export const getFeatureFlags = async (): Promise<FeatureFlag[]> => {
    const response = await apiClient.get<FeatureFlag[]>('/api/v1/admin/features');
    return response.data;
};

export const createFeatureFlag = async (data: FeatureFlagCreateRequest): Promise<FeatureFlag> => {
    const response = await apiClient.post<FeatureFlag>('/api/v1/admin/features', data);
    return response.data;
};

export const updateFeatureFlag = async (featureId: number, data: FeatureFlagUpdateRequest): Promise<FeatureFlag> => {
    const response = await apiClient.put<FeatureFlag>(`/api/v1/admin/features/${featureId}`, data);
    return response.data;
};

export const deleteFeatureFlag = async (featureId: number): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/features/${featureId}`);
};

// ==========================================
// LLM Configuration
// ==========================================

export const getLLMProviders = async (): Promise<LLMProvidersResponse> => {
    const response = await apiClient.get<LLMProvidersResponse>('/api/v1/admin/llm/providers');
    return response.data;
};

export const getLLMConfigs = async (tenantId?: string): Promise<LLMConfig[]> => {
    const params = tenantId ? { tenant_id: tenantId } : {};
    const response = await apiClient.get<LLMConfig[]>('/api/v1/admin/llm/configs', { params });
    return response.data;
};

export const createLLMConfig = async (data: LLMConfigCreateRequest): Promise<LLMConfig> => {
    const response = await apiClient.post<LLMConfig>('/api/v1/admin/llm/configs', data);
    return response.data;
};

export const updateLLMConfig = async (configId: number, data: LLMConfigUpdateRequest): Promise<LLMConfig> => {
    const response = await apiClient.put<LLMConfig>(`/api/v1/admin/llm/configs/${configId}`, data);
    return response.data;
};

export const deleteLLMConfig = async (configId: number): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/llm/configs/${configId}`);
};

export const getLLMApiKeys = async (tenantId?: string): Promise<LLMApiKey[]> => {
    const params = tenantId ? { tenant_id: tenantId } : {};
    const response = await apiClient.get<LLMApiKey[]>('/api/v1/admin/llm/keys', { params });
    return response.data;
};

export const createLLMApiKey = async (data: LLMApiKeyCreateRequest): Promise<LLMApiKey> => {
    const response = await apiClient.post<LLMApiKey>('/api/v1/admin/llm/keys', data);
    return response.data;
};

export const deleteLLMApiKey = async (keyId: number): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/llm/keys/${keyId}`);
};

export const getLLMPricing = async (providerName?: string): Promise<ModelPricing[]> => {
    const params = providerName ? { provider_name: providerName } : {};
    const response = await apiClient.get<ModelPricing[]>('/api/v1/admin/llm/pricing', { params });
    return response.data;
};

export const createLLMPricing = async (data: ModelPricingCreateRequest): Promise<ModelPricing> => {
    const response = await apiClient.post<ModelPricing>('/api/v1/admin/llm/pricing', data);
    return response.data;
};

export const updateLLMPricing = async (pricingId: number, data: ModelPricingUpdateRequest): Promise<ModelPricing> => {
    const response = await apiClient.put<ModelPricing>(`/api/v1/admin/llm/pricing/${pricingId}`, data);
    return response.data;
};

export const deleteLLMPricing = async (pricingId: number): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/llm/pricing/${pricingId}`);
};

export const getLLMUsageStats = async (params?: {
    tenant_id?: string;
    start_date?: string;
    end_date?: string;
    group_by?: string;
}): Promise<UsageStatsRow[]> => {
    const response = await apiClient.get<UsageStatsRow[]>('/api/v1/admin/llm/usage', { params });
    return response.data;
};
